/**
 * Recommended actions: the task pipeline in brief, one row with the top card of each phase (Figma
 * 5684:18358), under the filter bar and "View more" (4377:6742). The full board is its own page.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { shortName } from "@/components/team/roles";
import { cx } from "@/components/ui";
import { identity } from "@/lib/api";
import { initials } from "@/lib/format";
import { useGrowthBoard } from "@/lib/growth";
import type {
  GrowthBoardFilters,
  GrowthNiche,
  GrowthSeverity,
  GrowthSourceType,
  GrowthTask,
  GrowthTaskStatus,
  User,
} from "@/lib/types";

import {
  dateFilter,
  dateSince,
  FilterBar,
  type FilterSpec,
  sourceTypeFilter,
  topicFilter,
  urgencyFilter,
  useFilterValues,
} from "../filters";
import { pillButton, RECOMMENDATIONS_PATH, SectionHeading, taskPath, WidgetError, WidgetLoading } from "./ui";

/** The pipeline's phases, in order, with the colour of each one's bar. */
export const PHASES: { id: GrowthTaskStatus; label: string; bar: string }[] = [
  { id: "todo", label: "To Do", bar: "bg-fg-subtle" },
  { id: "in_review", label: "In review", bar: "bg-[#129970]" },
  { id: "needs_approval", label: "Needs approval", bar: "bg-brand-500" },
  { id: "live", label: "Live", bar: "bg-[#016cec]" },
  { id: "on_hold", label: "On hold", bar: "bg-[#ec0105]" },
];

/** Whose cards: everyone's, the person's own, or nobody's yet (Figma 5684:18589). */
const scopeFilter: FilterSpec = {
  key: "scope",
  label: "Assigned to",
  icon: "user",
  initial: "all",
  bare: true,
  options: [
    { value: "all", label: "All" },
    { value: "mine", label: "Mine" },
    { value: "unassigned", label: "Unassigned" },
  ],
};

/** The pipeline's filters: whose cards, the shared ones, and its own phases as Status. */
export function pipelineFilters(niche: GrowthNiche | undefined): FilterSpec[] {
  return [
    scopeFilter,
    dateFilter,
    topicFilter(niche),
    sourceTypeFilter,
    urgencyFilter,
    {
      key: "status",
      label: "Status",
      icon: "activity-heart",
      multiple: true,
      options: PHASES.map((p) => ({ value: p.id, label: p.label })),
    },
  ];
}

/** The filter bar's choices as the board API's query. */
export function boardQuery(values: Record<string, string[]>): GrowthBoardFilters {
  return {
    since: dateSince(values.date?.[0]),
    topic: values.topic,
    source_type: values.source_type as GrowthSourceType[],
    urgency: values.urgency as GrowthSeverity[],
    status: values.status as GrowthTaskStatus[],
    scope: values.scope?.[0] as GrowthBoardFilters["scope"],
  };
}

export function RecommendedActions({ niche }: { niche: GrowthNiche | undefined }) {
  const specs = pipelineFilters(niche);
  const values = useFilterValues(specs);
  const board = useGrowthBoard(boardQuery(values));
  const people = usePeople();
  // Narrowed by anything other than the defaults (everyone's cards from the last 30 days).
  const filtered = specs.some((s) => (values[s.key] ?? []).some((v) => v !== s.initial));
  const total = board.data ? Object.values(board.data.counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <section aria-labelledby="gi-actions" className="flex flex-col gap-5">
      <SectionHeading
        id="gi-actions"
        title="Recommended actions"
        action={
          <Link to={RECOMMENDATIONS_PATH} className={pillButton}>
            View more
          </Link>
        }
      />
      <FilterBar label="Filter recommended actions" specs={specs} />
      {board.isPending ? (
        <WidgetLoading label="Loading recommended actions" />
      ) : board.isError ? (
        <WidgetError error={board.error} onRetry={() => void board.refetch()} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {PHASES.map((phase) => {
              // The API lists the most urgent first: the top of each phase's stack.
              const top = board.data.items.find((t) => t.status === phase.id);
              return (
                <section key={phase.id} aria-label={phase.label} className="flex min-w-0 flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <h3 className="flex items-center gap-1.5 text-[11.5px] text-fg-subtle">
                      <span className="font-semibold tracking-[0.575px] uppercase">{phase.label}</span>{" "}
                      <span>{board.data.counts[phase.id]}</span>
                    </h3>
                    <span aria-hidden className={cx("h-[3px] rounded-[2px]", phase.bar)} />
                  </div>
                  {top ? (
                    <Link
                      to={taskPath(top.id)}
                      aria-label={top.title}
                      className="rounded-xl transition-shadow hover:shadow-[0_4px_12px_rgba(51,31,20,0.08)] focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:outline-none"
                    >
                      <TaskCard task={top} people={people} />
                    </Link>
                  ) : (
                    total > 0 && (
                      <p className="rounded-xl border border-dashed border-border px-3 py-3 text-xs text-fg-subtle">
                        {filtered ? "No cards match these filters" : "Nothing here"}
                      </p>
                    )
                  )}
                </section>
              );
            })}
          </div>
          {total === 0 && (
            <div className="rounded-[14px] border border-dashed border-border px-6 py-8 text-center">
              <p className="text-sm font-semibold text-fg">
                {filtered ? "No tasks match these filters" : "No tasks yet"}
              </p>
              <p className="mx-auto mt-1 max-w-md text-[13px] text-fg-muted">
                {filtered
                  ? "Try another topic, urgency or status, or reset the filters."
                  : "Open an insight and create a task from its recommended actions. It appears here and moves from To Do to Live."}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Everyone in the business, by user id, for assignee names and pictures. */
export function usePeople(): Map<string, User> {
  const { orgId } = useOrganization();
  const members = useQuery({ queryKey: ["members", orgId], queryFn: () => identity.members(orgId) });
  return new Map((members.data?.items ?? []).map((m) => [m.user.id, m.user]));
}

/** The pipeline frame's urgency pills: High amber, Medium green, Low quiet (written "Low" there). */
const PRIORITY: Record<GrowthSeverity, { label: string; tone: string }> = {
  high: { label: "HIGH", tone: "bg-[#d07100]/15 text-[#d07100]" },
  medium: { label: "MEDIUM", tone: "bg-[#12a97e]/15 text-[#12a97e]" },
  low: { label: "Low", tone: "bg-[#faf6ef] text-fg-subtle" },
};

const pill = "rounded-full px-2 py-[3px] text-[10px] leading-normal font-semibold";

/** The most faces a card stacks; anyone after them is counted in the name ("Amara K +2"). */
const FACES = 3;

/** Who has a card, in a few words: "Unassigned", "Amara K", "Amara K and Liam O", "Amara K +2". */
export function assigneesLabel(ids: string[], people: Map<string, User>): string {
  const name = (id: string) => {
    const u = people.get(id);
    return u ? shortName(u.display_name ?? "", u.email) : "Someone in the business";
  };
  const [first, second] = ids;
  if (first === undefined) return "Unassigned";
  if (second === undefined) return name(first);
  if (ids.length === 2) return `${name(first)} and ${name(second)}`;
  return `${name(first)} +${ids.length - 1}`;
}

/** A card: title, description, who has it, its urgency and whether it was rolled back ("action card"). */
export function TaskCard({ task, people }: { task: GrowthTask; people: Map<string, User> }) {
  const ids = task.assignee_user_ids;
  return (
    <article className="flex flex-col items-start gap-2 rounded-xl border border-border bg-surface p-3">
      <h4 className="text-[13px] leading-[1.35] font-semibold text-fg">{task.title}</h4>
      {task.description && (
        <p className="line-clamp-3 text-xs leading-[1.45] text-fg-muted">{task.description}</p>
      )}
      <div className="flex min-w-0 items-center gap-2">
        {ids.length === 0 ? (
          <Avatar user={null} assigned={false} />
        ) : (
          <span className="flex shrink-0">
            {ids.slice(0, FACES).map((id, n) => (
              <span key={id} className={cx("rounded-full ring-2 ring-surface", n > 0 && "-ml-1.5")}>
                <Avatar user={people.get(id)} assigned />
              </span>
            ))}
          </span>
        )}
        <span className="truncate text-xs text-fg-subtle">{assigneesLabel(ids, people)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={cx(pill, PRIORITY[task.priority].tone)}>{PRIORITY[task.priority].label}</span>
        {task.rolled_back && (
          <span className={cx(pill, "bg-brand-100 text-brand-500 uppercase")}>Rolled back</span>
        )}
        {task.status === "needs_approval" && task.draft_status === "accepted" && (
          // approved; it goes Live once the website is published with the change
          <span className={cx(pill, "bg-[#12a97e]/15 text-[#12a97e] uppercase")}>
            Approved · publish to finish
          </span>
        )}
        {task.attachment_count > 0 && (
          <span className={cx(pill, "bg-[#faf6ef] text-fg-subtle")}>
            {task.attachment_count} file{task.attachment_count === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </article>
  );
}

function Avatar({ user, assigned }: { user: User | null | undefined; assigned: boolean }) {
  if (!assigned) return <span aria-hidden className="size-5 shrink-0 rounded-full bg-[#f8b6a0]" />;
  if (user?.avatar_url) {
    return (
      <img src={user.avatar_url} alt="" className="size-5 shrink-0 rounded-full bg-muted object-cover" />
    );
  }
  return (
    <span
      aria-hidden
      className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[8.5px] font-semibold text-fg-muted"
    >
      {user ? initials(user.display_name || user.email) : "?"}
    </span>
  );
}
